import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConstants } from '../commons/app-constants';
import { Page } from '../commons/models/page';
import { offlineDb, getLastSyncMarker, setLastSyncMarker, scopedKey } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';

// Shared merge logic for the offline delta-sync feature (Increment 6). Deliberately NOT part
// of ListCacheService/DetailCacheService — those two are "replace whatever's cached with the
// latest online response," which is exactly wrong for a delta payload (a delta response is a
// partial update, never a full replacement). Each entity service calls this alongside its
// existing cache read/write, not through it.
export interface DeltaMergeConfig<T> {
  getId: (row: T) => number;
  // ISO-ish date string — compared with string comparison (localeCompare), so must be a format
  // where lexical order matches chronological order (e.g. ISO-8601 `createdDate` fields already
  // used throughout this codebase qualify).
  getCreatedDate: (row: T) => string;
  // BusinessPartner-only: soft-delete surfaces as isActive flipping to false on an otherwise
  // ordinary delta row (see BusinessPartnerServiceImpl's modifiedSince override of the isActive
  // filter) — when this returns false for a row, it's evicted from the window immediately,
  // same as a tombstoned id. Omit for entities with no soft-delete concept.
  getIsActive?: (row: T) => boolean;
  windowSize: number;
}

// A row carrying _offlineClientUuid is a local, not-yet-synced create (or, once synced, an
// entity whose write is still resolving via SyncEngineService) — it must never be silently
// dropped by a delta merge, since that would lose real unsynced user work. Simpler than the
// original plan's idMap-aware check (which would only protect rows with no *matching* synced
// entry yet) — this protects any such row unconditionally, which costs nothing since these are
// rare and don't count against the window's size cap anyway (mirrors how
// ProductService.createOffline already appends without trimming).
// Exported for MasterCacheService — the dropdown-list cache shares the same masterCache keys
// ('products', 'buyers', 'suppliers', 'loadingSites') as this offline-pending tracking, so its
// refresh-on-reconnect must protect these rows the same way a delta merge does.
export function isProtected(row: unknown): boolean {
  return !!(row as { _offlineClientUuid?: string })?._offlineClientUuid;
}

export interface DeltaMergeResult<T> {
  content: T[];
  // Rows actually removed from the window this pass — via a hard-delete tombstone (deletedIds)
  // or a soft-delete flip (getIsActive false). Only rows that were genuinely present in
  // cachedWindow appear here (a deletedId for a row outside this device's window has nothing to
  // evict). Callers use these to purge the corresponding detailCache entries (full detail
  // record, and for a TU, its whole tallysheet cascade) — otherwise a deleted record's cached
  // data just sits in IndexedDB forever, since evicting it from the LIST alone doesn't free that
  // space. Empty array, never undefined, when nothing was evicted.
  evictedRows: T[];
}

// cachedWindow: the current top-N cached rows (already sorted by createdDate desc).
// deltaRows: rows returned by the modifiedSince query — a mix of "existing row, just edited"
//   and "genuinely new since last sync." A row absent from BOTH deltaRows and deletedIds is
//   simply untouched and stays exactly where it is.
// deletedIds: ids from GET /api/v1/sync/deletions — hard-deleted rows to evict outright.
export function mergeDeltaIntoWindow<T>(
  cachedWindow: T[],
  deltaRows: T[],
  deletedIds: number[],
  config: DeltaMergeConfig<T>,
): DeltaMergeResult<T> {
  const protectedRows = cachedWindow.filter(isProtected);
  const realRows = cachedWindow.filter((r) => !isProtected(r));

  const byId = new Map<number, T>(realRows.map((r) => [config.getId(r), r]));
  const evictedRows: T[] = [];
  const deletedSet = new Set(deletedIds);
  deletedSet.forEach((id) => {
    const row = byId.get(id);
    if (row) {
      evictedRows.push(row);
      byId.delete(id);
    }
  });

  const newCandidates: T[] = [];
  deltaRows.forEach((row) => {
    const id = config.getId(row);
    if (deletedSet.has(id)) return; // belt-and-suspenders — shouldn't normally overlap
    if (config.getIsActive && !config.getIsActive(row)) {
      const existing = byId.get(id);
      if (existing) evictedRows.push(existing); // soft-deleted (BusinessPartner) — evict same as a hard delete
      byId.delete(id);
      return;
    }
    if (byId.has(id)) {
      byId.set(id, row); // in-place update — same rank, a lastModifiedDate bump alone never reshuffles
    } else {
      newCandidates.push(row); // candidate for the "genuinely new since last sync" branch below
    }
  });

  let currentRows = Array.from(byId.values());

  // A genuinely new record only earns a slot by being more recent (by createdDate) than the
  // window's current oldest entry — otherwise it's real, but doesn't rank into what the live
  // list would actually show for that slot, so it's correctly left out.
  newCandidates.forEach((candidate) => {
    if (currentRows.length < config.windowSize) {
      currentRows.push(candidate);
      return;
    }
    let oldestIdx = 0;
    let oldestDate = config.getCreatedDate(currentRows[0]);
    for (let i = 1; i < currentRows.length; i++) {
      const d = config.getCreatedDate(currentRows[i]);
      if (d < oldestDate) {
        oldestDate = d;
        oldestIdx = i;
      }
    }
    if (config.getCreatedDate(candidate) > oldestDate) {
      currentRows[oldestIdx] = candidate;
    }
  });

  currentRows.sort((a, b) => config.getCreatedDate(b).localeCompare(config.getCreatedDate(a)));

  return { content: [...protectedRows, ...currentRows], evictedRows };
}

// GET /api/v1/sync/deletions — hard-deleted ids since `since`, for entities with a tombstone
// table (see EntityDeletionLogService on the backend). Best-effort: a failed fetch just leaves
// this pass's window holding a row that was actually deleted server-side, self-correcting on the
// next sync rather than blocking the rest of this one.
export function fetchDeletions(http: HttpClient, entityType: string, since: string): Observable<number[]> {
  const params = new HttpParams().set('entityType', entityType).set('since', since);
  return http.get<number[]>(`${AppConstants.API_URL}api/v1/sync/deletions`, { params }).pipe(
    catchError(() => of([])),
  );
}

export interface DeltaListSyncOptions<T> {
  entity: string; // marker key, e.g. 'products' — see getLastSyncMarker/setLastSyncMarker
  cacheKey: string; // masterCache key the merged Page<T> is written back under
  // Called with the stored marker (null on first-ever sync, or whenever there's no valid cached
  // page to merge into) — the entity service decides its own page size per call (a small
  // windowSize fetch when modifiedSince is null, a larger one when it isn't, so a delta response
  // with more changed rows than the window size still comes back in a single page).
  fetchPage: (modifiedSince: string | null) => Observable<HttpResponse<Page<T>>>;
  deletionsEntityType: string | null; // null => no tombstone table (BusinessPartner soft-deletes instead)
  mergeConfig: DeltaMergeConfig<T>;
  http: HttpClient;
}

export interface DeltaSyncResult<T> {
  page: Page<T>;
  // False on a first-ever sync (or whenever there was nothing valid to merge into) — callers
  // that need to distinguish "every row is effectively new" from "only touchedIds actually
  // changed" (see TU's tallysheet-skip optimization in OfflinePrefetchService) branch on this.
  isDelta: boolean;
  // Ids present in the raw fetch response — on a delta sync this is exactly the set of rows
  // genuinely new-or-modified since the marker; on a non-delta (first-ever) sync it's every row
  // in the page, since there's no prior state to call "unchanged." Lets a caller skip re-fetching
  // expensive per-row sub-resources (TU's full tallysheet) for rows that didn't actually change.
  touchedIds: Set<number>;
  // Rows removed this pass because they were deleted (or, for BusinessPartner, soft-deleted)
  // server-side AND were present in this device's previously-cached window. Empty on a
  // non-delta (first-ever) sync. Callers purge the corresponding detailCache entries for these
  // so a deleted record's cached data doesn't just sit in IndexedDB forever.
  evictedRows: T[];
}

// Orchestrates one entity's list sync: a first-ever sync (or a marker with nothing usable
// cached under it) trusts the full response as-is — there's nothing to merge into. Every sync
// after that fetches only what changed since the stored marker and merges it into the existing
// cached window via mergeDeltaIntoWindow, rather than overwriting the whole cached list the way
// ListCacheService.cacheThrough would (exactly wrong for a partial delta response). Returns the
// merged page (or null if offline sync isn't applicable or the fetch failed) so the caller can
// still run its own per-row detail/photo prefetch off the same rows.
export async function runDeltaListSync<T>(opts: DeltaListSyncOptions<T>): Promise<DeltaSyncResult<T> | null> {
  if (!isOfflineCapableNative()) return null;

  const cacheKey = scopedKey(opts.cacheKey);
  const since = await getLastSyncMarker(opts.entity);
  const cachedEntry = since ? await offlineDb.masterCache.get(cacheKey) : undefined;
  const cachedPage = cachedEntry?.data as Page<T> | undefined;
  const isDelta = !!(since && cachedPage);

  let response: HttpResponse<Page<T>>;
  try {
    response = await firstValueFrom(opts.fetchPage(isDelta ? since : null));
  } catch {
    return null;
  }
  const freshPage = response.body;
  if (!freshPage) return null;
  const serverTime = response.headers.get('X-Server-Time');
  const touchedIds = new Set(freshPage.content.map(opts.mergeConfig.getId));

  let mergedPage: Page<T>;
  let evictedRows: T[] = [];
  if (!isDelta) {
    mergedPage = freshPage;
  } else {
    let deletedIds: number[] = [];
    if (opts.deletionsEntityType) {
      deletedIds = await firstValueFrom(fetchDeletions(opts.http, opts.deletionsEntityType, since!));
    }
    const mergeResult = mergeDeltaIntoWindow(cachedPage!.content, freshPage.content, deletedIds, opts.mergeConfig);
    mergedPage = { ...cachedPage!, content: mergeResult.content };
    evictedRows = mergeResult.evictedRows;
  }

  await offlineDb.masterCache.put({ key: cacheKey, data: mergedPage, fetchedAt: Date.now() });
  if (serverTime) await setLastSyncMarker(opts.entity, serverTime);
  return { page: mergedPage, isDelta, touchedIds, evictedRows };
}

// Self-heals a real, permanent gap in the delta-sync design above: both eviction paths —
// getIsActive (soft-delete) and fetchDeletions (hard-delete tombstones) — only ever see changes
// that happened AFTER the stored sync marker. A row deleted/deactivated BEFORE this device's
// marker was ever established stays stuck in the cached window forever, retried on every single
// prefetch cycle with no self-correction possible via the delta path alone (found 2026-08-04: a
// real device showing identical "Not found" 404s for the same 3 rows across app sessions 35+
// minutes apart, for the SAME long-lived user — not a different-user cache leak, which is
// already handled separately by wipeOfflineDataIfDifferentUser).
//
// Call this from a cached row's own detail-fetch error handler when the server returns 404: an
// authoritative "this doesn't exist" from the server should always win over a stale local list
// entry, regardless of why the delta mechanism missed it. Best-effort — a failed read/write here
// just means this one row gets retried again next cycle, same as it already was.
export async function evictStaleRowFromCache<T>(
  cacheKey: string,
  id: number,
  getId: (row: T) => number,
): Promise<void> {
  if (!isOfflineCapableNative()) return;
  try {
    const key = scopedKey(cacheKey);
    const entry = await offlineDb.masterCache.get(key);
    const page = entry?.data as Page<T> | undefined;
    if (!page?.content?.length) return;
    const filtered = page.content.filter((row) => getId(row) !== id);
    if (filtered.length === page.content.length) return; // row wasn't in this cached window — nothing to do
    await offlineDb.masterCache.put({ ...entry!, data: { ...page, content: filtered } });
  } catch {
    // best-effort, see comment above
  }
}
